import { useId } from 'react'

import Link from '@/components/link-component'
import CtaLink, { type CtaLinkProps } from '@/components/ui/cta-link'
import HubspotCTA, { type HubspotCTAProps } from '@/components/ui/hubspot-cta'
import CtaAskAi, { type CtaAskAiProps } from '@/components/ui/cta-ask-ai'

import type { TypeComponentCtaLink, TypeComponentHubspotCta, TypeComponentCtaAskAi } from '@/types/ctf'

type CtaLinkPropsWithType = {
  type?: 'ctaLink'
} & CtaLinkProps

type CtaLinkEntry = TypeComponentCtaLink<'WITHOUT_UNRESOLVABLE_LINKS'>
type CtaLinkWithType = Partial<Omit<CtaLinkEntry['fields'], 'link' | 'style'>> & {
  type: 'componentCtaLink'
  link?: Partial<CtaLinkEntry['fields']['link']> | null
} & CtaLinkProps

type HubspotCtaEntry = TypeComponentHubspotCta<'WITHOUT_UNRESOLVABLE_LINKS'>
type HubspotCtaWithType = Omit<HubspotCtaEntry['fields'], 'style'> & {
  type: 'componentHubspotCta'
} & HubspotCTAProps

type CtaAskAiEntry = TypeComponentCtaAskAi<'WITHOUT_UNRESOLVABLE_LINKS'>
type CtaAskAiWithType = Omit<CtaAskAiEntry['fields'], 'style'> & {
  type: 'componentCtaAskAi'
} & CtaAskAiProps

export type DynamicActionProps = (CtaLinkPropsWithType | CtaLinkWithType | HubspotCtaWithType | CtaAskAiWithType) & {
  id?: string
  label?: string
  nonCtaLink?: boolean
  asButton?: boolean
  className?: string
}

/** Renders the correct CTA component based on the type */
export default function DynamicAction(fields: DynamicActionProps) {
  const allProps = (fields as any).fields ?? fields

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { entryId, ...props } = allProps

  const contentfulId = fields.id ? fields.id : props.link?.sys?.id
  const uid = useId() + contentfulId

  switch (props.type) {
    case 'componentHubspotCta':
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { type: hubspotTypename, ...hubspotCtaProps } = props as HubspotCtaWithType
      return <HubspotCTA {...hubspotCtaProps}>{props.label}</HubspotCTA>
    case 'componentCtaLink':
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { link, type: contentfulCtaLinkTypename, ...contentfulCtaLinkProps } = props as CtaLinkWithType

      return (
        props?.link?.fields?.url && (
          <CtaLink {...contentfulCtaLinkProps} id={uid} to={link?.fields?.url}>
            {props.label}
          </CtaLink>
        )
      )
    case 'componentCtaAskAi':
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { type: askAiTypename, ...askAiProps } = props as CtaAskAiWithType
      return props.prompt && <CtaAskAi {...askAiProps} />
    case 'ctaLink':
    default:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { type: ctaLinkTypename, ...ctaLinkProps } = props as CtaLinkWithType

      //avoids using the CtaLink component and complex css inheritance
      return ctaLinkProps.nonCtaLink ? (
        <Link
          to={ctaLinkProps.link?.fields?.url}
          className={`${ctaLinkProps.className} ${ctaLinkProps.plausibleEventName}`}
          id={uid}
          data-testid="cta-link"
          tabIndex={ctaLinkProps.tabindex}
          onClick={ctaLinkProps.onClick || undefined}
        >
          {ctaLinkProps.label || ctaLinkProps.children}
        </Link>
      ) : (
        <CtaLink
          className={ctaLinkProps.className}
          to={ctaLinkProps.link?.fields?.url}
          {...ctaLinkProps}
          id={uid}
          tabIndex={ctaLinkProps.tabindex}
        >
          {ctaLinkProps.label || ctaLinkProps.children}
        </CtaLink>
      )
  }
}
